import React, { MutableRefObject, MouseEvent, useState, useEffect } from 'react';

import Crop from '@/lib/crop';

export type CanvasProps = {
  mouseUp?: MouseEvent<HTMLDivElement>;
  baseDraw: () => void;
  onStartDrawing: () => void;
  onDrawZoomBox: (x: number, y: number, w: number, h: number) => void;
  // onDrawCropBox: (x: number, y: number, w: number, h: number) => void;
  onWheelDown: () => void;
  onCanvasClick: (coords: Point) => void;
  // viewOtherObjects: boolean;
  drawClip: Clip | null;
  // cropClip: Clip | null;
  containerWidth: number;
  containerHeight: number;
  ratio: number;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>;
};

type Clip = [number, number, number, number];
// type Pair<A, B> = [A, B];
type Point = [number, number];

const MIN_DRAW_SIZE = 15;
// const MIN_CROP_SIZE = 400;
const MIN_ZOOM_WIDTH = 300;
// const MAX_IMAGE_HEIGHT = 600;

const Canvas = React.forwardRef<HTMLCanvasElement, CanvasProps>((props, ref) => {
  const [startCoordinates, setStartCoordinates] = useState<Point | null>(null);
  const [currentCoordinates, setCurrentCordinates] = useState<Point | null>(null);
  const [isZooming, setIsZooming] = useState(false);

  const [zoomSizeOk, setZoomSizeOk] = useState(false);
  // const [realCropMinSize, setRealCropMinSize] = useState(MIN_CROP_SIZE);

  const {
    mouseUp,
    baseDraw,
    onStartDrawing,
    onDrawZoomBox,
    onWheelDown,
    onCanvasClick,
    // viewOtherObjects,
    drawClip,
    containerWidth,
    containerHeight,
    ratio,
    setIsDrawing,
  } = props;

  const getClickCoordinates = (evt: MouseEvent<HTMLCanvasElement> | MouseEvent<HTMLDivElement>) => {
    if (ref) {
      const rect = (ref as MutableRefObject<HTMLCanvasElement>).current.getBoundingClientRect();
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;

      return [x, y] as Point;
    }
    return [0, 0] as Point;
  };

  const getCtx = () => {
    let ctx: CanvasRenderingContext2D | null = null;
    const hasCanvas = ref && (ref as MutableRefObject<HTMLCanvasElement>).current;
    if (hasCanvas) {
      ctx = (ref as MutableRefObject<HTMLCanvasElement>).current.getContext('2d');
    }
    return ctx;
  };

  const handleMouseDown = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    const coords = getClickCoordinates(evt);
    // console.log("start cords", coords)
    setStartCoordinates(coords);

    setIsZooming(true);
    setIsDrawing(true);

    const ctx = getCtx();
    const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      baseDraw();
    }

    onStartDrawing();
  };

  const handleClick = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    const coords = getClickCoordinates(evt);
    onCanvasClick(coords);
    // setSize(0);
  };

  const handleMouseMove = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (isZooming) {
      const coords = getClickCoordinates(evt);
      setCurrentCordinates(coords);
    }
  };

  useEffect(() => {
    if (mouseUp) {
      handleMouseUp(mouseUp);
    }
  }, [mouseUp]);

  const handleMouseUp = (evt: MouseEvent<HTMLCanvasElement> | MouseEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (isZooming && startCoordinates) {
      const ctx = getCtx();
      const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
      if (ctx && startCoordinates && currentCoordinates) {
        const w = currentCoordinates[0] - startCoordinates[0];
        const h = currentCoordinates[1] - startCoordinates[1];

        if (isZooming) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          // setPolygon([startCoordinates, coords]);

          const crop = new Crop(startCoordinates[0], startCoordinates[1], w, h);
          crop.keepRatio(ratio);
          crop.keepInBounds(canvas.width, canvas.height);
          if (zoomSizeOk) {
            onDrawZoomBox(crop.x, crop.y, crop.w, crop.h);
          }
        }
      }

      if (isZooming) {
        setIsZooming(false);
        setIsDrawing(false);
      }
    }
  };

  const calculateRealCropSize = (x: number, y: number, w: number, h: number, currentDrawClip: Clip) => {
    if (currentDrawClip) {
      const [bx, by, ex, ey] = currentDrawClip;
      const clipWidth = ex - bx;
      const clipHeight = ey - by;

      // CROP relative coordinates
      const zoomStartX = Math.floor(x);
      const zoomStartY = Math.floor(y);
      const zoomEndX = Math.floor(x + w);
      const zoomEndY = Math.floor(y + h);

      // CLIP relative coordinates
      const clipStartX = Math.floor((zoomStartX / containerWidth) * (clipWidth as number));
      const clipStartY = Math.floor((zoomStartY / containerHeight) * (clipHeight as number));
      const clipEndX = Math.floor((zoomEndX / containerWidth) * (clipWidth as number));
      const clipEndY = Math.floor((zoomEndY / containerHeight) * (clipHeight as number));

      // IMAGE coordinates
      const realStartX = bx + clipStartX;
      const realStartY = by + clipStartY;
      const realEndX = bx + clipEndX;
      const realEndY = by + clipEndY;

      const realWidth = realEndX - realStartX;
      const realHeight = realEndY - realStartY;

      return [realWidth, realHeight];
    }
    return null;
  };

  const handleMouseWheel = (evt: any) => {
    // @ts-ignore
    if (evt.deltaY > 0) {
      onWheelDown();
    }
  };

  useEffect(() => {
    const ctx = getCtx();
    const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
    if (ctx && startCoordinates && currentCoordinates) {
      // calculate size of the polygon
      const w = currentCoordinates[0] - startCoordinates[0];
      const h = currentCoordinates[1] - startCoordinates[1];

      const absW = Math.abs(w);
      const absH = Math.abs(h);

      const crop = new Crop(startCoordinates[0], startCoordinates[1], w, h);

      if (isZooming) {
        if (absW >= MIN_DRAW_SIZE || absH >= MIN_DRAW_SIZE) {
          crop.keepRatio(ratio);
          crop.keepInBounds(canvas.width, canvas.height);

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          drawRect(ctx, crop.x, crop.y, crop.w, crop.h);
        }
      }

      if (drawClip) {
        const realCropSize = calculateRealCropSize(crop.x, crop.y, crop.w, crop.h, drawClip);
        if (realCropSize) {
          const realCropWidth = realCropSize[0];

          if (isZooming) {
            if (realCropWidth < MIN_ZOOM_WIDTH) {
              setZoomSizeOk(false);
            } else {
              setZoomSizeOk(true);
            }
          }
        }
      }
    }
  }, [currentCoordinates]);

  const drawRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
    ctx.lineWidth = 2;
    ctx.strokeStyle = isZooming ? (zoomSizeOk ? '#FFF' : '#F00') : '#FFF';
    ctx.strokeRect(x, y, w, h);
  };

  return (
    <canvas
      ref={ref}
      style={{ zIndex: 200, height: '100%', width: '100%' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={handleMouseWheel}
      onClick={handleClick}
    />
  );
});

export default Canvas;
